import React, { useEffect } from 'react'
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  useColorModeValue,
  useColorMode,
  Icon,
  Flex,
  Badge,
  Divider,
} from '@chakra-ui/react'
import { motion, useAnimation } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {
  FaCheckCircle,
  FaTimesCircle,
  FaSchool,
  FaExclamationTriangle,
} from 'react-icons/fa'
import LEColorConstants, {
  basicTextColor,
} from '@/lib/constants/colorConstants'
import { BiTrendingDown, BiTrendingUp } from 'react-icons/bi'

const comparison = {
  prepared: {
    title: 'Schools Using Our CBT Platform',
    color: LEColorConstants.colorCheck,
    bgColor: 'rgba(3, 237, 125, 0.1)',
    icon: FaCheckCircle,
    benefits: [
      {
        title: 'Student Performance',
        description: '40% higher pass rates in external exams',
        icon: BiTrendingUp,
      },
      {
        title: 'Exam Confidence',
        description: 'Students comfortable with CBT interface',
        icon: FaCheckCircle,
      },
      {
        title: 'School Reputation',
        description: 'Known as innovative, forward-thinking institution',
        icon: FaSchool,
      },
      {
        title: 'Parent Trust',
        description: "Parents confident in school's technology adoption",
        icon: FaCheckCircle,
      },
      {
        title: 'Cost Efficiency',
        description: 'Reduced paper costs and exam administration',
        icon: FaCheckCircle,
      },
      {
        title: 'Time Management',
        description: 'Instant results and automated grading',
        icon: FaCheckCircle,
      },
    ],
  },
  unprepared: {
    title: 'Schools Still Using Traditional Methods',
    color: '#EF4444',
    bgColor: 'rgba(239, 68, 68, 0.1)',
    icon: FaTimesCircle,
    benefits: [
      {
        title: 'Student Performance',
        description: 'Students struggle with unfamiliar CBT interface',
        icon: BiTrendingDown,
      },
      {
        title: 'Exam Anxiety',
        description: 'High stress levels during actual CBT exams',
        icon: FaExclamationTriangle,
      },
      {
        title: 'School Reputation',
        description: 'Seen as outdated compared to tech-forward schools',
        icon: FaTimesCircle,
      },
      {
        title: 'Parent Concerns',
        description: "Parents question school's preparedness for future",
        icon: FaExclamationTriangle,
      },
      {
        title: 'Hidden Costs',
        description: 'Last-minute training and panic preparations',
        icon: FaTimesCircle,
      },
      {
        title: 'Manual Processes',
        description: 'Time-consuming paper-based assessments',
        icon: FaTimesCircle,
      },
    ],
  },
}

export const ComparisonSection = () => {
  const { colorMode } = useColorMode()
  const bgColor = useColorModeValue('gray.50', 'gray.900')
  const cardBg = useColorModeValue('white', 'gray.800')

  const controls = useAnimation()
  const { inView, ref } = useInView({ threshold: 0.2 })

  useEffect(() => {
    if (inView) {
      controls.start('visible')
    }
  }, [controls, inView])

  return (
    <Box
      bg={bgColor}
      py="5rem"
      ref={ref}
      as={motion.div}
      animate={controls}
      initial={false}
      whileInView="visible"
      viewport={{ once: true }}
    >
      <Container maxW="90rem" mx="auto" px={['1rem', '3rem', '4rem', '6rem']}>
        <VStack spacing="4rem">
          {/* Header */}
          <Box
            textAlign="center"
            as={motion.div}
            variants={{
              hidden: { opacity: 0, y: 50 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
            }}
          >
            <Heading
              fontSize={['2rem', '2.5rem', '3rem']}
              fontWeight="bold"
              color={basicTextColor[colorMode]}
              mb="1rem"
            >
              Two Paths:{' '}
              <Text as="span" color={LEColorConstants.primary}>
                Which Will
              </Text>{' '}
              <Text as="span" color={LEColorConstants.secondary}>
                You Choose?
              </Text>
            </Heading>

            <Text
              fontSize={['1.1rem', '1.3rem']}
              color={basicTextColor[colorMode]}
              maxW="4xl"
              mx="auto"
              fontWeight="medium"
            >
              The difference between schools that prepare for CBT and those that
              don't is dramatic
            </Text>
          </Box>

          {/* Comparison Grid */}
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing="3rem"
            w="full"
            as={motion.div}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.3,
                  delayChildren: 0.2,
                },
              },
            }}
          >
            {/* Prepared Schools */}
            <Box
              as={motion.div}
              variants={{
                hidden: { opacity: 0, x: -50 },
                visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
              }}
            >
              <Box
                bg={cardBg}
                borderRadius="20px"
                p="2.5rem"
                h="full"
                position="relative"
                border="3px solid"
                borderColor={comparison.prepared.color}
                boxShadow="xl"
                _hover={{
                  transform: 'translateY(-5px)',
                  boxShadow: '2xl',
                }}
                transition="all 0.3s ease"
              >
                {/* Header */}
                <Flex align="center" mb="2rem">
                  <Icon
                    as={comparison.prepared.icon}
                    boxSize="40px"
                    color={comparison.prepared.color}
                    mr="1rem"
                  />
                  <VStack align="start" spacing="0">
                    <Badge
                      colorScheme="green"
                      variant="solid"
                      fontSize="0.8rem"
                      px="1rem"
                      py="0.2rem"
                      borderRadius="full"
                    >
                      THE SMART CHOICE
                    </Badge>
                    <Heading
                      fontSize={['1.3rem', '1.5rem']}
                      color={basicTextColor[colorMode]}
                      fontWeight="bold"
                      mt="0.5rem"
                    >
                      {comparison.prepared.title}
                    </Heading>
                  </VStack>
                </Flex>

                {/* Benefits */}
                <VStack spacing="1.5rem" align="stretch">
                  {comparison.prepared.benefits.map((benefit, index) => (
                    <Box
                      key={index}
                      bg={comparison.prepared.bgColor}
                      p="1rem"
                      borderRadius="12px"
                      border="1px solid"
                      borderColor={comparison.prepared.color + '30'}
                    >
                      <HStack spacing="1rem" align="start">
                        <Icon
                          as={benefit.icon}
                          boxSize="20px"
                          color={comparison.prepared.color}
                          mt="0.2rem"
                          flexShrink="0"
                        />
                        <VStack align="start" spacing="0.2rem" flex="1">
                          <Text
                            fontWeight="bold"
                            color={basicTextColor[colorMode]}
                            fontSize="1rem"
                          >
                            {benefit.title}
                          </Text>
                          <Text
                            color={basicTextColor[colorMode]}
                            fontSize="0.9rem"
                            opacity="0.8"
                          >
                            {benefit.description}
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  ))}
                </VStack>

                {/* Success Metrics */}
                <Box
                  mt="2rem"
                  p="1.5rem"
                  bg="linear-gradient(135deg, rgba(3, 237, 125, 0.1) 0%, rgba(0, 155, 255, 0.1) 100%)"
                  borderRadius="12px"
                  textAlign="center"
                >
                  <Text
                    fontSize="1.1rem"
                    fontWeight="bold"
                    color={comparison.prepared.color}
                    mb="0.5rem"
                  >
                    🏆 PROVEN RESULTS
                  </Text>
                  <HStack justify="space-around" flexWrap="wrap">
                    <VStack spacing="0">
                      <Text
                        fontSize="1.5rem"
                        fontWeight="bold"
                        color={comparison.prepared.color}
                      >
                        +40%
                      </Text>
                      <Text fontSize="0.8rem" color="gray.600">
                        Pass Rates
                      </Text>
                    </VStack>
                    <VStack spacing="0">
                      <Text
                        fontSize="1.5rem"
                        fontWeight="bold"
                        color={comparison.prepared.color}
                      >
                        95%
                      </Text>
                      <Text fontSize="0.8rem" color="gray.600">
                        Satisfaction
                      </Text>
                    </VStack>
                    <VStack spacing="0">
                      <Text
                        fontSize="1.5rem"
                        fontWeight="bold"
                        color={comparison.prepared.color}
                      >
                        -60%
                      </Text>
                      <Text fontSize="0.8rem" color="gray.600">
                        Exam Anxiety
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              </Box>
            </Box>

            {/* Unprepared Schools */}
            <Box
              as={motion.div}
              variants={{
                hidden: { opacity: 0, x: 50 },
                visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
              }}
            >
              <Box
                bg={cardBg}
                borderRadius="20px"
                p="2.5rem"
                h="full"
                position="relative"
                border="3px solid"
                borderColor={comparison.unprepared.color}
                boxShadow="xl"
                opacity="0.9"
              >
                {/* Header */}
                <Flex align="center" mb="2rem">
                  <Icon
                    as={comparison.unprepared.icon}
                    boxSize="40px"
                    color={comparison.unprepared.color}
                    mr="1rem"
                  />
                  <VStack align="start" spacing="0">
                    <Badge
                      colorScheme="red"
                      variant="solid"
                      fontSize="0.8rem"
                      px="1rem"
                      py="0.2rem"
                      borderRadius="full"
                    >
                      RISKY PATH
                    </Badge>
                    <Heading
                      fontSize={['1.3rem', '1.5rem']}
                      color={basicTextColor[colorMode]}
                      fontWeight="bold"
                      mt="0.5rem"
                    >
                      {comparison.unprepared.title}
                    </Heading>
                  </VStack>
                </Flex>

                {/* Drawbacks */}
                <VStack spacing="1.5rem" align="stretch">
                  {comparison.unprepared.benefits.map((benefit, index) => (
                    <Box
                      key={index}
                      bg={comparison.unprepared.bgColor}
                      p="1rem"
                      borderRadius="12px"
                      border="1px solid"
                      borderColor={comparison.unprepared.color + '30'}
                    >
                      <HStack spacing="1rem" align="start">
                        <Icon
                          as={benefit.icon}
                          boxSize="20px"
                          color={comparison.unprepared.color}
                          mt="0.2rem"
                          flexShrink="0"
                        />
                        <VStack align="start" spacing="0.2rem" flex="1">
                          <Text
                            fontWeight="bold"
                            color={basicTextColor[colorMode]}
                            fontSize="1rem"
                          >
                            {benefit.title}
                          </Text>
                          <Text
                            color={basicTextColor[colorMode]}
                            fontSize="0.9rem"
                            opacity="0.8"
                          >
                            {benefit.description}
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  ))}
                </VStack>

                {/* Warning Metrics */}
                <Box
                  mt="2rem"
                  p="1.5rem"
                  bg="linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(245, 101, 101, 0.1) 100%)"
                  borderRadius="12px"
                  textAlign="center"
                >
                  <Text
                    fontSize="1.1rem"
                    fontWeight="bold"
                    color={comparison.unprepared.color}
                    mb="0.5rem"
                  >
                    ⚠️ THE COST OF WAITING
                  </Text>
                  <HStack justify="space-around" flexWrap="wrap">
                    <VStack spacing="0">
                      <Text
                        fontSize="1.5rem"
                        fontWeight="bold"
                        color={comparison.unprepared.color}
                      >
                        -25%
                      </Text>
                      <Text fontSize="0.8rem" color="gray.600">
                        Pass Rates
                      </Text>
                    </VStack>
                    <VStack spacing="0">
                      <Text
                        fontSize="1.5rem"
                        fontWeight="bold"
                        color={comparison.unprepared.color}
                      >
                        73%
                      </Text>
                      <Text fontSize="0.8rem" color="gray.600">
                        Unprepared
                      </Text>
                    </VStack>
                    <VStack spacing="0">
                      <Text
                        fontSize="1.5rem"
                        fontWeight="bold"
                        color={comparison.unprepared.color}
                      >
                        +150%
                      </Text>
                      <Text fontSize="0.8rem" color="gray.600">
                        Stress Levels
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              </Box>
            </Box>
          </SimpleGrid>

          {/* Divider */}
          <Divider borderColor={LEColorConstants.primary} borderWidth="2px" />

          {/* Call to Action */}
          <Box
            textAlign="center"
            maxW="4xl"
            as={motion.div}
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.6, delay: 0.8 },
              },
            }}
          >
            <Heading
              fontSize={['2rem', '2.5rem']}
              fontWeight="bold"
              color={basicTextColor[colorMode]}
              mb="1rem"
            >
              The Choice is{' '}
              <Text as="span" color={LEColorConstants.secondary}>
                Crystal Clear
              </Text>
            </Heading>

            <Text
              fontSize={['1.1rem', '1.3rem']}
              color={basicTextColor[colorMode]}
              mb="2rem"
              lineHeight="1.6"
            >
              Don't let your students be part of the 73% who struggle with CBT.
              Join the forward-thinking schools that are already preparing their
              students for success.
            </Text>

            <Box
              bg="linear-gradient(135deg, #009BFF 0%, #FF6E06 100%)"
              p="2rem"
              borderRadius="16px"
              color="white"
              fontWeight="bold"
              fontSize={['1.2rem', '1.4rem']}
            >
              Every day you wait, your students fall further behind.
              <br />
              <Text as="span" fontSize={['1.4rem', '1.6rem']}>
                Start preparing them today with 500 FREE credits!
              </Text>
            </Box>
          </Box>
        </VStack>
      </Container>
    </Box>
  )
}
